/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021 (original work) Open Assessment Technologies SA
 */

import { update as updateRecoverableError, reset as resetRecoverableErrors } from '@/store/recoverableErrors';
import { initWebSocket } from '@/core/webSocket/webSocketHandler';
import { recoverableErrors as errorKeys } from '@/core/utils/consts';
import { get } from 'svelte/store';
import { activeGroup as activeGroupStore } from '@/store/navigation';
import env from '@/config/env';

const recoverableErrorsManager = {
    noConnectionLimit: 30000,

    noConnectionTimeout: null,

    /**
     * Handles init/sessions possible recoverable errors. If more than one
     * error is present, no retry is attempt. If only one
     * error is present, tries to recover from it.
     * @param {Array} errors - Array of errors to handle, errors must be keys of @recoverableErrors
     * @param {String} activeGroup - context id of the group to retry request
     */
    handleErrors: function (errors, activeGroup) {
        if (errors.length > 1) {
            this.setMultiError(errors);
        } else {
            this.setSingleError(errors[0], activeGroup);
        }
    },

    /**
     * Updates recoverableError store with provided errors
     * @param {Array} errors - Array of errors
     */
    setMultiError: function (errors) {
        updateRecoverableError(errors, {
            status: true,
            retrying: false
        });
    },

    /**
     * Updates recoverableError store and retries request three times.
     * Afer retries, updates recoverableError store and if error was
     * solved updates app state (if no new error) or restarts
     * error handling (if new errors were received)
     * @param {String} errorKey - Error to handle
     * @param {String} activeGroup - Context id to recover data from
     */
    setSingleError(errorKey, activeGroup) {
        updateRecoverableError(errorKey, {
            status: true,
            retrying: true
        });
        initWebSocket(env("WS_URL"), activeGroup);
    },

    /**
     * Updates internetConnection error and creates retrying timeout
     */
    setOffline: function () {
        updateRecoverableError(errorKeys.internetConnection, {
            status: true,
            retrying: true
        });

        this.noConnectionTimeout = setTimeout(() => {
            updateRecoverableError(errorKeys.internetConnection, {
                status: true,
                retrying: false
            });
        }, this.noConnectionLimit);
    },

    /**
     * Updates internetConnection error and clears retrying timeout,
     * then requests data for current context
     */
    setOnline: function () {
        updateRecoverableError(errorKeys.internetConnection, {
            status: false,
            retrying: false
        });
        this.reset();
        clearTimeout(this.noConnectionTimeout);
        const activeGroup = get(activeGroupStore);
        initWebSocket(env("WS_URL"), activeGroup);
    },

    /**
     * Reset recoverableErrors store
     * @param {Array} errors - Array of strings. Strings must be a valid @errorKeys value
     */
    reset: function (errors) {
        resetRecoverableErrors(errors);
    }
};

// //DOM Events : online/offline
window.addEventListener('online', function () {
    recoverableErrorsManager.setOnline();
});
window.addEventListener('offline', function () {
    recoverableErrorsManager.setOffline();
});

export default recoverableErrorsManager;
